import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import Header from "../layouts/Header";

const emptyMaster = { club_code: "", club_name: "", club_description: "", club_status: "active" };
const emptyOffering = {
  club_id: "", club_name: "", club_description: "", club_gender: "m",
  club_quota: 30, club_status: "active", lead_t_id: "", assistant_t_ids: []
};

const escHtml = (str) => String(str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const levelLabel = (level) => (level === "jr" ? "ม.ต้น" : level === "sr" ? "ม.ปลาย" : "ทุกระดับ");

const messageFor = (error) => {
  const code = error.response?.data?.code;
  const map = {
    CLUB_CODE_EXISTS: "รหัสชุมนุมนี้มีอยู่แล้ว",
    OFFERING_EXISTS: "เปิดชุมนุมนี้สำหรับปีและเพศที่เลือกแล้ว",
    LEAD_ADVISOR_REQUIRED: "กรุณากำหนดครูผู้ดูแลหลัก 1 คน",
    QUOTA_BELOW_ENROLLED: "โควตาใหม่ต่ำกว่าจำนวนสมาชิกปัจจุบัน",
    GENDER_LOCKED: "ไม่สามารถเปลี่ยนเพศได้หลังมีสมาชิก",
    CLUB_FULL: "ชุมนุมเต็มโควตาแล้ว",
    GENDER_MISMATCH: "เพศของนักเรียนไม่ตรงกับชุมนุม",
    ALREADY_ENROLLED: "นักเรียนมีชุมนุมในปีนี้แล้ว",
    SAME_CLUB: "นักเรียนอยู่ในชุมนุมนี้อยู่แล้ว",
  };
  return map[code] || error.response?.data?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่";
};

export default function ClubManagement() {
  const { setLoading } = useLoading();
  const [refs, setRefs] = useState({ years: [], teachers: [], periods: [] });
  const [yearId, setYearId] = useState("");
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState({ summary: {}, offerings: [], recentSessions: [] });
  const [masters, setMasters] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [masterModal, setMasterModal] = useState(false);
  const [masterForm, setMasterForm] = useState(emptyMaster);
  const [editingMaster, setEditingMaster] = useState(null);
  const [offeringModal, setOfferingModal] = useState(false);
  const [offeringForm, setOfferingForm] = useState(emptyOffering);
  const [editingOffering, setEditingOffering] = useState(null);
  const [membersOffering, setMembersOffering] = useState(null);
  const [members, setMembers] = useState([]);
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [copyFromYear, setCopyFromYear] = useState("");
  const [sessionRounds, setSessionRounds] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomDetail, setRoomDetail] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [unassignedQuery, setUnassignedQuery] = useState("");
  const [unassignedClass, setUnassignedClass] = useState("");
  const [unassignedGender, setUnassignedGender] = useState("");
  const [roundDate, setRoundDate] = useState("");
  const [roundSemester, setRoundSemester] = useState("1");
  const [roundPeriodId, setRoundPeriodId] = useState("");
  const [autoModal, setAutoModal] = useState(false);
  const [autoRaiseQuota, setAutoRaiseQuota] = useState(false);
  const [autoPreview, setAutoPreview] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  useEffect(() => {
    axiosInstance.get("/api/clubs/references")
      .then(({ data }) => {
        const stored = localStorage.getItem("yearId");
        const selected = data.years.find(y => String(y.year_id) === String(stored)) || data.years[0];
        setRefs(prev => ({ ...prev, years: data.years, teachers: data.teachers }));
        if (selected) setYearId(String(selected.year_id));
      })
      .catch(() => Swal.fire("ผิดพลาด", "โหลดข้อมูลอ้างอิงไม่สำเร็จ", "error"));
  }, []);

  const loadAll = useCallback(async () => {
    if (!yearId) return;
    setLoading(true);
    try {
      const [refRes, dashRes, masterRes, offerRes, roundRes, unassignedRes, roomRes] = await Promise.all([
        axiosInstance.get("/api/clubs/references", { params: { year_id: yearId } }),
        axiosInstance.get(`/api/clubs/years/${yearId}/dashboard`),
        axiosInstance.get("/api/clubs/masters"),
        axiosInstance.get(`/api/clubs/years/${yearId}/offerings`),
        axiosInstance.get(`/api/clubs/years/${yearId}/session-rounds`),
        axiosInstance.get(`/api/clubs/years/${yearId}/unassigned-students`),
        axiosInstance.get(`/api/clubs/years/${yearId}/rooms`),
      ]);
      setRefs(refRes.data);
      setDashboard(dashRes.data);
      setMasters(masterRes.data);
      setOfferings(offerRes.data);
      setSessionRounds(roundRes.data);
      setUnassigned(unassignedRes.data);
      setRooms(roomRes.data);
      setRoomDetail(null);
    } catch (error) {
      Swal.fire("ผิดพลาด", messageFor(error), "error");
    } finally {
      setLoading(false);
    }
  }, [yearId, setLoading]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const selectedYear = refs.years.find(y => String(y.year_id) === String(yearId));
  const unassignedClasses = [...new Set(unassigned.map(s => s.c_class))];
  const unassignedFiltered = unassigned.filter(student => {
    const q = unassignedQuery.trim().toLowerCase();
    const matchQuery = !q
      || String(student.st_idnumber || "").toLowerCase().includes(q)
      || String(student.st_name || "").toLowerCase().includes(q);
    return matchQuery
      && (!unassignedClass || String(student.c_class) === unassignedClass)
      && (!unassignedGender || student.st_gen === unassignedGender);
  });

  const roomClasses = [...new Set(rooms.map(room => room.c_class))];
  const roomTotals = rooms.reduce((acc, room) => ({
    total: acc.total + Number(room.total_students),
    enrolled: acc.enrolled + Number(room.enrolled_students),
    unassigned: acc.unassigned + Number(room.unassigned_students)
  }), { total: 0, enrolled: 0, unassigned: 0 });

  const openRoom = async (room) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/api/clubs/years/${yearId}/rooms/${room.r_id}/students`);
      setRoomDetail(data);
    } catch (error) {
      Swal.fire("ผิดพลาด", messageFor(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const printRoom = () => {
    if (!roomDetail?.students.length) return;
    const { room, students } = roomDetail;
    const withClub = students.filter(s => s.club_year_id).length;

    const rows = students.map((student, index) => `<tr>
        <td class="c">${index + 1}</td>
        <td class="c">${escHtml(student.st_idnumber)}</td>
        <td>${escHtml(student.st_name)}</td>
        <td class="c">${student.st_gen === "m" ? "ชาย" : "หญิง"}</td>
        <td>${student.club_year_id ? escHtml(student.club_name) : "- ยังไม่มีชุมนุม -"}</td>
        <td>${escHtml(student.lead_advisor || "")}</td>
      </tr>`).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>ชุมนุมของนักเรียน ${escHtml(room.c_class)}/${escHtml(room.r_no)}</title>
      <style>
        @page { size: A4 portrait; margin: 10mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: 'Cordia New', 'TH Sarabun New', Tahoma, Arial, sans-serif; font-size: 16px; color: #000; }
        h3, h4 { text-align: center; margin: 0 0 4px 0; }
        h3 { font-size: 18px; }
        h4 { font-size: 16px; font-weight: normal; }
        .meta { font-size: 14px; margin: 6px 0 8px 0; display: flex; justify-content: space-between; }
        table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        th, td { border: 1px solid #000; padding: 2px 4px; overflow: hidden; word-wrap: break-word; }
        thead { display: table-header-group; }
        thead th { text-align: center; background: #f0f0f0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        tbody tr { height: 22px; }
        .c { text-align: center; }
        .sign { margin-top: 20px; font-size: 14px; text-align: right; }
      </style></head>
      <body onload="window.print()">
        <h3>โรงเรียนวัฒนธรรมอิสลาม</h3>
        <h4>ชุมนุมของนักเรียน ${escHtml(room.c_class)}/${escHtml(room.r_no)} ปีการศึกษา ${escHtml(selectedYear?.year_year)}</h4>
        <div class="meta">
          <span>นักเรียน ${students.length} คน</span>
          <span>มีชุมนุม ${withClub} คน | ยังไม่มีชุมนุม ${students.length - withClub} คน</span>
        </div>
        <table>
          <colgroup>
            <col style="width:7%;" /><col style="width:13%;" /><col style="width:28%;" />
            <col style="width:8%;" /><col style="width:26%;" /><col style="width:18%;" />
          </colgroup>
          <thead><tr>
            <th>ลำดับ</th><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>เพศ</th><th>ชุมนุม</th><th>ครูผู้ดูแล</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="sign">พิมพ์เมื่อ ${new Date().toLocaleString("th-TH")}</div>
      </body></html>`;

    const win = window.open("", "_blank", "width=1000,height=900");
    if (!win) return Swal.fire("พิมพ์ไม่ได้", "กรุณาอนุญาต popup ของเบราว์เซอร์", "warning");
    win.document.write(html);
    win.document.close();
  };

  const removeFromClub = async (student) => {
    const ok = await Swal.fire({
      title: "นำออกจากชุมนุม?",
      html: `${escHtml(student.st_name)}<br/><small>${escHtml(student.club_name)}</small>`,
      icon: "warning", showCancelButton: true,
      confirmButtonText: "นำออก", cancelButtonText: "ยกเลิก"
    });
    if (!ok.isConfirmed) return;
    setLoading(true);
    try {
      await axiosInstance.put(`/api/clubs/enrollments/${student.club_enrollment_id}/withdraw`);
      const [detailRes, roomRes, unassignedRes, offerRes, dashRes] = await Promise.all([
        axiosInstance.get(`/api/clubs/years/${yearId}/rooms/${roomDetail.room.r_id}/students`),
        axiosInstance.get(`/api/clubs/years/${yearId}/rooms`),
        axiosInstance.get(`/api/clubs/years/${yearId}/unassigned-students`),
        axiosInstance.get(`/api/clubs/years/${yearId}/offerings`),
        axiosInstance.get(`/api/clubs/years/${yearId}/dashboard`),
      ]);
      setRoomDetail(detailRes.data);
      setRooms(roomRes.data);
      setUnassigned(unassignedRes.data);
      setOfferings(offerRes.data);
      setDashboard(dashRes.data);
      Swal.fire({ icon: "success", title: "นำออกแล้ว", timer: 1000, showConfirmButton: false });
    } catch (error) {
      Swal.fire("นำออกไม่สำเร็จ", messageFor(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const printUnassigned = () => {
    if (!unassignedFiltered.length) return;
    const males = unassignedFiltered.filter(s => s.st_gen === "m").length;
    const females = unassignedFiltered.length - males;
    const conditions = [
      unassignedClass ? `ชั้น ${unassignedClass}` : "ทุกชั้น",
      unassignedGender ? (unassignedGender === "m" ? "เฉพาะชาย" : "เฉพาะหญิง") : "ทุกเพศ",
      unassignedQuery.trim() ? `คำค้น "${unassignedQuery.trim()}"` : null
    ].filter(Boolean).join(" | ");

    const rows = unassignedFiltered.map((student, index) => `<tr>
        <td class="c">${index + 1}</td>
        <td class="c">${escHtml(student.st_idnumber)}</td>
        <td>${escHtml(student.st_name)}</td>
        <td class="c">${escHtml(student.c_class)}/${escHtml(student.r_no)}</td>
        <td class="c">${student.st_gen === "m" ? "ชาย" : "หญิง"}</td>
        <td></td>
      </tr>`).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>รายชื่อนักเรียนที่ยังไม่มีชุมนุม ปีการศึกษา ${escHtml(selectedYear?.year_year)}</title>
      <style>
        @page { size: A4 portrait; margin: 10mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: 'Cordia New', 'TH Sarabun New', Tahoma, Arial, sans-serif; font-size: 16px; color: #000; }
        h3, h4 { text-align: center; margin: 0 0 4px 0; }
        h3 { font-size: 18px; }
        h4 { font-size: 16px; font-weight: normal; }
        .meta { font-size: 14px; margin: 6px 0 8px 0; display: flex; justify-content: space-between; }
        table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        th, td { border: 1px solid #000; padding: 2px 4px; overflow: hidden; word-wrap: break-word; }
        thead { display: table-header-group; }
        thead th { text-align: center; background: #f0f0f0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        tbody tr { height: 22px; }
        .c { text-align: center; }
        .sign { margin-top: 20px; font-size: 14px; text-align: right; }
      </style></head>
      <body onload="window.print()">
        <h3>โรงเรียนวัฒนธรรมอิสลาม</h3>
        <h4>รายชื่อนักเรียนที่ยังไม่มีชุมนุม ปีการศึกษา ${escHtml(selectedYear?.year_year)}</h4>
        <div class="meta">
          <span>เงื่อนไข: ${escHtml(conditions)}</span>
          <span>รวม ${unassignedFiltered.length} คน (ชาย ${males} / หญิง ${females})</span>
        </div>
        <table>
          <colgroup>
            <col style="width:7%;" /><col style="width:13%;" /><col style="width:33%;" />
            <col style="width:20%;" /><col style="width:9%;" /><col style="width:18%;" />
          </colgroup>
          <thead><tr>
            <th>ลำดับ</th><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ชั้น</th><th>เพศ</th><th>ชุมนุมที่เลือก</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="sign">พิมพ์เมื่อ ${new Date().toLocaleString("th-TH")}</div>
      </body></html>`;

    const win = window.open("", "_blank", "width=1000,height=900");
    if (!win) return Swal.fire("พิมพ์ไม่ได้", "กรุณาอนุญาต popup ของเบราว์เซอร์", "warning");
    win.document.write(html);
    win.document.close();
  };

  const loadAutoPreview = useCallback(async (raiseQuota) => {
    setAutoBusy(true);
    try {
      const { data } = await axiosInstance.get(`/api/clubs/years/${yearId}/auto-assign/preview`, {
        params: { allow_quota_increase: raiseQuota ? 1 : 0 }
      });
      setAutoPreview(data);
    } catch (error) {
      setAutoPreview(null);
      Swal.fire("ดูตัวอย่างไม่สำเร็จ", messageFor(error), "error");
    } finally {
      setAutoBusy(false);
    }
  }, [yearId]);

  const openAutoAssign = () => {
    setAutoPreview(null);
    setAutoRaiseQuota(false);
    setAutoModal(true);
    loadAutoPreview(false);
  };

  const toggleAutoQuota = (checked) => {
    setAutoRaiseQuota(checked);
    loadAutoPreview(checked);
  };

  const runAutoAssign = async () => {
    const ok = await Swal.fire({
      title: "ยืนยันจัดชุมนุมอัตโนมัติ?",
      html: `จะเพิ่มนักเรียน <b>${autoPreview.assignments.length}</b> คนเข้าชุมนุม`
        + (autoPreview.quota_changes.length
          ? `<br/>และเพิ่มโควตา <b>${autoPreview.quota_changes.length}</b> ชุมนุม`
          : ""),
      icon: "question", showCancelButton: true,
      confirmButtonText: "จัดเลย", cancelButtonText: "ยกเลิก"
    });
    if (!ok.isConfirmed) return;
    setAutoBusy(true);
    try {
      const { data } = await axiosInstance.post(`/api/clubs/years/${yearId}/auto-assign`, {
        allow_quota_increase: autoRaiseQuota
      });
      setAutoModal(false);
      setAutoPreview(null);
      await loadAll();
      Swal.fire("จัดชุมนุมแล้ว", `เพิ่มนักเรียน ${data.assigned} คน`
        + (data.quota_updates ? ` | เพิ่มโควตา ${data.quota_updates} ชุมนุม (${data.added_seats} ที่นั่ง)` : "")
        + (data.leftover ? ` | ยังไม่มีชุมนุม ${data.leftover} คน` : ""), "success");
    } catch (error) {
      Swal.fire("จัดชุมนุมไม่สำเร็จ", messageFor(error), "error");
    } finally {
      setAutoBusy(false);
    }
  };

  const saveMaster = async (event) => {
    event.preventDefault();
    try {
      if (editingMaster) {
        await axiosInstance.put(`/api/clubs/masters/${editingMaster.club_id}`, masterForm);
      } else {
        await axiosInstance.post("/api/clubs/masters", masterForm);
      }
      setMasterModal(false);
      await loadAll();
      Swal.fire({ icon: "success", title: "บันทึกแล้ว", timer: 1000, showConfirmButton: false });
    } catch (error) { Swal.fire("บันทึกไม่สำเร็จ", messageFor(error), "error"); }
  };

  const openMaster = (row = null) => {
    setEditingMaster(row);
    setMasterForm(row ? {
      club_code: row.club_code, club_name: row.club_name,
      club_description: row.club_description || "", club_status: row.club_status
    } : emptyMaster);
    setMasterModal(true);
  };

  const openOffering = (row = null) => {
    setEditingOffering(row);
    if (row) {
      const lead = row.advisors.find(a => a.advisor_role === "lead");
      setOfferingForm({
        club_id: row.club_id, club_name: row.club_name,
        club_description: row.club_description || "", club_gender: row.club_gender,
        club_quota: row.club_quota, club_status: row.club_status,
        lead_t_id: lead?.t_id || "",
        assistant_t_ids: row.advisors.filter(a => a.advisor_role === "assistant").map(a => String(a.t_id))
      });
    } else {
      setOfferingForm(emptyOffering);
    }
    setOfferingModal(true);
  };

  const selectMaster = (clubId) => {
    const master = masters.find(m => String(m.club_id) === String(clubId));
    setOfferingForm(prev => ({
      ...prev, club_id: clubId,
      club_name: master?.club_name || "", club_description: master?.club_description || ""
    }));
  };

  const saveOffering = async (event) => {
    event.preventDefault();
    const advisors = [
      ...(offeringForm.lead_t_id
        ? [{ t_id: Number(offeringForm.lead_t_id), advisor_role: "lead" }]
        : []),
      ...offeringForm.assistant_t_ids
        .filter(id => String(id) !== String(offeringForm.lead_t_id))
        .map(id => ({ t_id: Number(id), advisor_role: "assistant" }))
    ];
    const payload = { ...offeringForm, year_id: Number(yearId), advisors };
    try {
      if (editingOffering) {
        await axiosInstance.put(`/api/clubs/offerings/${editingOffering.club_year_id}`, payload);
      } else {
        await axiosInstance.post("/api/clubs/offerings", payload);
      }
      setOfferingModal(false);
      await loadAll();
      Swal.fire({ icon: "success", title: "บันทึกแล้ว", timer: 1000, showConfirmButton: false });
    } catch (error) { Swal.fire("บันทึกไม่สำเร็จ", messageFor(error), "error"); }
  };

  const openSessionRound = async (event) => {
    event.preventDefault();
    try {
      await axiosInstance.post(`/api/clubs/years/${yearId}/session-rounds`, {
        semester: roundSemester, session_date: roundDate, period_id: Number(roundPeriodId)
      });
      setRoundDate(""); setRoundPeriodId("");
      await loadAll();
      Swal.fire({ icon: "success", title: "เปิดรอบชุมนุมแล้ว", timer: 1200, showConfirmButton: false });
    } catch (error) { Swal.fire("เปิดรอบไม่สำเร็จ", messageFor(error), "error"); }
  };
  const copyYear = async () => {
    if (!copyFromYear || String(copyFromYear) === String(yearId)) return;
    try {
      const { data } = await axiosInstance.post("/api/clubs/offerings/copy-year", {
        from_year_id: Number(copyFromYear), to_year_id: Number(yearId)
      });
      await loadAll();
      Swal.fire("สำเร็จ", `คัดลอกชุมนุม ${data.copied} รายการ`, "success");
    } catch (error) { Swal.fire("คัดลอกไม่สำเร็จ", messageFor(error), "error"); }
  };

  const openMembers = async (offering) => {
    setMembersOffering(offering);
    setStudentQuery("");
    setStudentResults([]);
    const { data } = await axiosInstance.get(`/api/clubs/offerings/${offering.club_year_id}/members`);
    setMembers(data);
  };

  const closeMembers = () => {
    setMembersOffering(null);
    setMembers([]);
    setStudentResults([]);
    setStudentQuery("");
  };

  const reloadMembers = async () => {
    if (!membersOffering) return;
    const { data } = await axiosInstance.get(`/api/clubs/offerings/${membersOffering.club_year_id}/members`);
    setMembers(data);
  };

  const searchStudents = async () => {
    if (studentQuery.trim().length < 2) return;
    try {
      const { data } = await axiosInstance.get("/api/clubs/students/search", {
        params: { year_id: yearId, q: studentQuery.trim() }
      });
      setStudentResults(data);
    } catch (error) { Swal.fire("ค้นหาไม่สำเร็จ", messageFor(error), "error"); }
  };

  const addStudent = async (student) => {
    try {
      await axiosInstance.post(`/api/clubs/offerings/${membersOffering.club_year_id}/enrollments`, {
        st_id: student.st_id
      });
      await reloadMembers();
      await loadAll();
      setStudentResults([]);
      setStudentQuery("");
    } catch (error) { Swal.fire("เพิ่มไม่ได้", messageFor(error), "error"); }
  };

  const withdraw = async (member) => {
    const ok = await Swal.fire({
      title: "ถอนสมาชิก?", text: member.st_name, icon: "warning",
      showCancelButton: true, confirmButtonText: "ถอน", cancelButtonText: "ยกเลิก"
    });
    if (!ok.isConfirmed) return;
    try {
      await axiosInstance.put(`/api/clubs/enrollments/${member.club_enrollment_id}/withdraw`);
      await reloadMembers();
      await loadAll();
    } catch (error) { Swal.fire("ถอนไม่สำเร็จ", messageFor(error), "error"); }
  };

  const transfer = async (member, targetClubYearId) => {
    if (!targetClubYearId) return;
    try {
      await axiosInstance.put(`/api/clubs/enrollments/${member.club_enrollment_id}/transfer`, {
        target_club_year_id: Number(targetClubYearId)
      });
      await reloadMembers();
      await loadAll();
    } catch (error) { Swal.fire("ย้ายไม่สำเร็จ", messageFor(error), "error"); }
  };

  return (
    <>
      <Header />
      <main className="main main-app p-3 p-lg-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h4 className="main-title mb-1">จัดการชุมนุม</h4>
          <div className="text-secondary">แม่แบบ ชุมนุมรายปี สมาชิก ตารางเวลา และภาพรวม</div>
        </div>
        <Form.Select value={yearId} onChange={e => setYearId(e.target.value)} style={{ width: 190 }}>
          {refs.years.map(year => <option key={year.year_id} value={year.year_id}>ปีการศึกษา {year.year_year}</option>)}
        </Form.Select>
      </div>

      <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
        <Nav.Item><Nav.Link eventKey="dashboard">Dashboard</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="offerings">ชุมนุมประจำปี</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="unassigned">นักเรียนที่ยังไม่มีชุมนุม {unassigned.length > 0 && <Badge bg="danger">{unassigned.length}</Badge>}</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="rooms">ชั้นเรียน</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="masters">แม่แบบชุมนุม</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="settings">เปิดรอบเช็คชื่อ</Nav.Link></Nav.Item>
      </Nav>

      {tab === "dashboard" && <Dashboard data={dashboard} year={selectedYear} />}

      {tab === "masters" && (
        <Card className="card-one">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <Card.Title as="h6" className="mb-0">แม่แบบชุมนุม</Card.Title>
            <Button size="sm" onClick={() => openMaster()}><i className="ri-add-line" /> เพิ่มแม่แบบ</Button>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead><tr><th>รหัส</th><th>ชื่อชุมนุม</th><th>รายละเอียด</th><th>สถานะ</th><th /></tr></thead>
              <tbody>
                {masters.map(row => (
                  <tr key={row.club_id}>
                    <td>{row.club_code}</td><td>{row.club_name}</td><td>{row.club_description || "-"}</td>
                    <td><Badge bg={row.club_status === "active" ? "success" : "secondary"}>{row.club_status === "active" ? "ใช้งาน" : "ปิด"}</Badge></td>
                    <td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => openMaster(row)}>แก้ไข</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {tab === "offerings" && (
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">ชุมนุมปีการศึกษา {selectedYear?.year_year}</Card.Title>
            <div className="d-flex gap-2">
              <Form.Select size="sm" value={copyFromYear} onChange={e => setCopyFromYear(e.target.value)} style={{ width: 180 }}>
                <option value="">คัดลอกจากปี...</option>
                {refs.years.filter(y => String(y.year_id) !== String(yearId)).map(y => <option key={y.year_id} value={y.year_id}>{y.year_year}</option>)}
              </Form.Select>
              <Button size="sm" variant="outline-secondary" disabled={!copyFromYear} onClick={copyYear}>คัดลอก</Button>
              <Button size="sm" onClick={() => openOffering()}><i className="ri-add-line" /> เปิดชุมนุม</Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 align-middle">
              <thead><tr><th>ชุมนุม</th><th>เพศ</th><th>ครูผู้ดูแล</th><th>สมาชิก/โควตา</th><th>สถานะ</th><th /></tr></thead>
              <tbody>
                {offerings.map(row => (
                  <tr key={row.club_year_id}>
                    <td><div className="fw-semibold">{row.club_name}</div><small className="text-secondary">{row.club_code}</small></td>
                    <td><Badge bg={row.club_gender === "m" ? "primary" : "danger"}>{row.club_gender === "m" ? "ชาย" : "หญิง"}</Badge></td>
                    <td>{row.advisors.map(a => <div key={a.t_id}>{a.advisor_role === "lead" ? "หลัก: " : "ผู้ช่วย: "}{a.t_name}</div>)}</td>
                    <td>{row.enrolled} / {row.club_quota}</td>
                    <td><Badge bg={row.club_status === "active" ? "success" : "secondary"}>{row.club_status === "active" ? "เปิด" : "ปิด"}</Badge></td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" variant="outline-success" className="me-1" onClick={() => openMembers(row)}>สมาชิก</Button>
                      <Button size="sm" variant="outline-primary" onClick={() => openOffering(row)}>แก้ไข</Button>
                    </td>
                  </tr>
                ))}
                {!offerings.length && <tr><td colSpan="6" className="text-center py-4 text-secondary">ยังไม่มีชุมนุมในปีนี้</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {tab === "rooms" && !roomDetail && (
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">ชั้นเรียนทั้งหมด ปีการศึกษา {selectedYear?.year_year}</Card.Title>
            <div className="text-secondary">
              นักเรียน {roomTotals.total} คน | มีชุมนุม {roomTotals.enrolled} | ยังไม่มี {roomTotals.unassigned}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead><tr><th>ห้องเรียน</th><th className="text-center">นักเรียน</th><th className="text-center">มีชุมนุมแล้ว</th><th className="text-center">ยังไม่มีชุมนุม</th><th /></tr></thead>
              <tbody>
                {roomClasses.map(className => (
                  <React.Fragment key={className}>
                    <tr className="table-light"><td colSpan="5" className="fw-semibold">{className}</td></tr>
                    {rooms.filter(room => room.c_class === className).map(room => (
                      <tr key={room.r_id}>
                        <td>{room.c_class}/{room.r_no}</td>
                        <td className="text-center">{room.total_students}</td>
                        <td className="text-center">{room.enrolled_students}</td>
                        <td className="text-center">
                          {Number(room.unassigned_students) > 0
                            ? <Badge bg="danger">{room.unassigned_students}</Badge>
                            : <Badge bg="success">0</Badge>}
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" onClick={() => openRoom(room)}>ดูรายชื่อ</Button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {!rooms.length && <tr><td colSpan="5" className="text-center py-4 text-secondary">ยังไม่มีห้องเรียนในปีนี้</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {tab === "rooms" && roomDetail && (
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <Card.Title as="h6" className="mb-1">
                {roomDetail.room.c_class}/{roomDetail.room.r_no} ({roomDetail.students.length} คน)
              </Card.Title>
              <div className="text-secondary">
                มีชุมนุม {roomDetail.students.filter(s => s.club_year_id).length} | ยังไม่มี {roomDetail.students.filter(s => !s.club_year_id).length}
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" onClick={() => setRoomDetail(null)}>
                <i className="ri-arrow-left-line me-1" />กลับ
              </Button>
              <Button size="sm" variant="outline-primary" onClick={printRoom}>
                <i className="ri-printer-line me-1" />พิมพ์
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead><tr><th style={{ width: 60 }}>ลำดับ</th><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>เพศ</th><th>ชุมนุม</th><th>ครูผู้ดูแล</th><th /></tr></thead>
              <tbody>
                {roomDetail.students.map((student, index) => (
                  <tr key={student.st_id}>
                    <td>{index + 1}</td>
                    <td>{student.st_idnumber}</td>
                    <td>{student.st_name}</td>
                    <td>{student.st_gen === "m" ? "ชาย" : "หญิง"}</td>
                    <td>
                      {student.club_year_id
                        ? <>{student.club_name} <small className="text-secondary">{student.club_code}</small></>
                        : <Badge bg="danger">ยังไม่มีชุมนุม</Badge>}
                    </td>
                    <td>{student.lead_advisor || "-"}</td>
                    <td className="text-end">
                      {student.club_enrollment_id && (
                        <Button size="sm" variant="outline-danger" onClick={() => removeFromClub(student)}>
                          <i className="ri-user-unfollow-line me-1" />นำออก
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!roomDetail.students.length && <tr><td colSpan="7" className="text-center py-4 text-secondary">ไม่มีนักเรียนในห้องนี้</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {tab === "unassigned" && (
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">
              นักเรียนที่ยังไม่มีชุมนุม ปีการศึกษา {selectedYear?.year_year} ({unassignedFiltered.length} คน
              {" — "}ชาย {unassignedFiltered.filter(s => s.st_gen === "m").length}
              {" / "}หญิง {unassignedFiltered.filter(s => s.st_gen === "f").length})
            </Card.Title>
            <div className="d-flex gap-2">
              <Form.Select size="sm" value={unassignedClass} onChange={e => setUnassignedClass(e.target.value)} style={{ width: 150 }}>
                <option value="">ทุกชั้น</option>
                {unassignedClasses.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
              <Form.Select size="sm" value={unassignedGender} onChange={e => setUnassignedGender(e.target.value)} style={{ width: 130 }}>
                <option value="">ทุกเพศ</option>
                <option value="m">ชาย</option>
                <option value="f">หญิง</option>
              </Form.Select>
              <Form.Control size="sm" style={{ width: 220 }} placeholder="ค้นหารหัสหรือชื่อ" value={unassignedQuery} onChange={e => setUnassignedQuery(e.target.value)} />
              <Button size="sm" variant="outline-primary" disabled={!unassignedFiltered.length} onClick={printUnassigned}>
                <i className="ri-printer-line me-1" />พิมพ์
              </Button>
              <Button size="sm" variant="primary" disabled={!unassigned.length} onClick={openAutoAssign}>
                <i className="ri-magic-line me-1" />จัดชุมนุมอัตโนมัติ
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead><tr><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ชั้น</th><th>เพศ</th></tr></thead>
              <tbody>
                {unassignedFiltered.map((student, index) => (
                  <tr key={student.st_id}>
                    <td>{student.st_idnumber}</td>
                    <td>{student.st_name}</td>
                    <td>{student.c_class}/{student.r_no}</td>
                    <td>{student.st_gen === "m" ? "ชาย" : "หญิง"}</td>
                  </tr>
                ))}
                {!unassignedFiltered.length && (
                  <tr><td colSpan="4" className="text-center py-4 text-secondary">
                    {unassigned.length ? "ไม่พบนักเรียนตามเงื่อนไขที่ค้นหา" : "นักเรียนทุกคนมีชุมนุมแล้ว"}
                  </td></tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {tab === "settings" && (
        <Card className="card-one mt-3">
          <Card.Header><Card.Title as="h6" className="mb-0">เปิดรอบเช็คชื่อชุมนุม</Card.Title></Card.Header>
          <Card.Body>
            <Form onSubmit={openSessionRound} className="d-flex flex-wrap gap-2 align-items-end">
              <Form.Group><Form.Label>ภาคเรียน</Form.Label><Form.Select value={roundSemester} onChange={e => setRoundSemester(e.target.value)}><option value="1">1</option><option value="2">2</option></Form.Select></Form.Group>
              <Form.Group><Form.Label>วันที่</Form.Label><Form.Control type="date" required value={roundDate} onChange={e => setRoundDate(e.target.value)} /></Form.Group>
              <Form.Group><Form.Label>คาบ</Form.Label><Form.Select required value={roundPeriodId} onChange={e => setRoundPeriodId(e.target.value)}><option value="">เลือกคาบ</option>{refs.periods.map(p => <option key={p.period_id} value={p.period_id}>คาบ {p.period_order}</option>)}</Form.Select></Form.Group>
              <Button type="submit">เปิดรอบ</Button>
            </Form>
            {!!sessionRounds.length && <Table size="sm" className="mt-3 mb-0"><thead><tr><th>วันที่</th><th>ภาคเรียน</th><th>คาบ</th></tr></thead><tbody>{sessionRounds.map(r => <tr key={r.club_session_round_id}><td>{String(r.session_date).slice(0, 10)}</td><td>{r.semester}</td><td>{r.period_order}</td></tr>)}</tbody></Table>}
          </Card.Body>
        </Card>
      )}
      <Modal show={autoModal} onHide={() => setAutoModal(false)} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title>จัดชุมนุมอัตโนมัติ ปีการศึกษา {selectedYear?.year_year}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="alert alert-light border mb-3">
            <div className="fw-semibold mb-1">เกณฑ์การจัด</div>
            <ul className="mb-0 ps-3">
              <li>เพศของนักเรียนต้องตรงกับเพศของชุมนุม</li>
              <li>ระดับชั้นดูจากชื่อชุมนุม — ชุมนุมที่มีคำว่า “ม.ต้น” รับเฉพาะ ม.1-3, “ม.ปลาย” รับเฉพาะ ม.4-6 (ชื่อที่ไม่ระบุระดับรับได้ทุกชั้น)</li>
              <li>ไม่เกินโควตาของชุมนุม และเติมชุมนุมที่เหลือที่ว่างมากที่สุดก่อน เพื่อกระจายให้ทั่ว</li>
            </ul>
          </div>

          <Form.Check
            type="switch" id="auto-raise-quota" className="mb-3"
            label="เพิ่มโควตาอัตโนมัติเมื่อที่นั่งไม่พอ (กระจายตามสัดส่วนโควตาเดิมของแต่ละชุมนุม)"
            checked={autoRaiseQuota} disabled={autoBusy}
            onChange={e => toggleAutoQuota(e.target.checked)}
          />

          {autoBusy && !autoPreview && <div className="text-center py-4 text-secondary">กำลังคำนวณ...</div>}

          {autoPreview && (
            <>
              <div className="d-flex flex-wrap gap-3 mb-3">
                <Badge bg="primary" className="fs-6">จัดได้ {autoPreview.assignments.length} คน</Badge>
                <Badge bg={autoPreview.leftover.length ? "danger" : "success"} className="fs-6">
                  เหลือไม่มีชุมนุม {autoPreview.leftover.length} คน
                </Badge>
                {!!autoPreview.quota_changes.length && (
                  <Badge bg="warning" text="dark" className="fs-6">
                    เพิ่มโควตา {autoPreview.quota_changes.length} ชุมนุม
                    {" (+"}{autoPreview.quota_changes.reduce((sum, q) => sum + q.added_seats, 0)}{" ที่นั่ง)"}
                  </Badge>
                )}
              </div>

              <div className="fw-semibold mb-1">สรุปตามเพศและระดับชั้น</div>
              <Table size="sm" bordered className="mb-3 align-middle">
                <thead><tr><th>กลุ่ม</th><th className="text-center">นักเรียนค้าง</th><th className="text-center">ที่ว่างเดิม</th><th className="text-center">ขาด</th><th className="text-center">โควตาที่เพิ่ม</th></tr></thead>
                <tbody>
                  {autoPreview.buckets.map(bucket => (
                    <tr key={bucket.club_gender + bucket.level}>
                      <td>{bucket.club_gender === "m" ? "ชาย" : "หญิง"} {levelLabel(bucket.level)}</td>
                      <td className="text-center">{bucket.demand}</td>
                      <td className="text-center">{bucket.capacity}</td>
                      <td className="text-center">{bucket.short ? <span className="text-danger">{bucket.short}</span> : 0}</td>
                      <td className="text-center">{bucket.added_seats || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {!!autoPreview.quota_changes.length && (
                <>
                  <div className="fw-semibold mb-1">โควตาที่จะถูกเพิ่ม</div>
                  <Table size="sm" bordered hover className="mb-3 align-middle">
                    <thead><tr><th>ชุมนุม</th><th>เพศ</th><th>ระดับ</th><th className="text-center">โควตาเดิม</th><th className="text-center">เพิ่ม</th><th className="text-center">โควตาใหม่</th></tr></thead>
                    <tbody>
                      {autoPreview.quota_changes.map(change => (
                        <tr key={change.club_year_id}>
                          <td>{change.club_name}</td>
                          <td>{change.club_gender === "m" ? "ชาย" : "หญิง"}</td>
                          <td>{levelLabel(change.level)}</td>
                          <td className="text-center">{change.old_quota}</td>
                          <td className="text-center text-success">+{change.added_seats}</td>
                          <td className="text-center fw-semibold">{change.new_quota}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              <div className="fw-semibold mb-1">นักเรียนที่จะเข้าแต่ละชุมนุม</div>
              <Table size="sm" bordered hover className="mb-3 align-middle">
                <thead><tr><th>ชุมนุม</th><th>เพศ</th><th>ระดับ</th><th className="text-center">สมาชิกเดิม</th><th className="text-center">เพิ่ม</th><th className="text-center">รวม/โควตา</th></tr></thead>
                <tbody>
                  {autoPreview.club_assignments.map(club => (
                    <tr key={club.club_year_id}>
                      <td>{club.club_name}</td>
                      <td>{club.club_gender === "m" ? "ชาย" : "หญิง"}</td>
                      <td>{levelLabel(club.level)}</td>
                      <td className="text-center">{club.enrolled_before}</td>
                      <td className="text-center text-success">+{club.assigned}</td>
                      <td className="text-center">{club.enrolled_after} / {club.quota}</td>
                    </tr>
                  ))}
                  {!autoPreview.club_assignments.length && (
                    <tr><td colSpan="6" className="text-center py-3 text-secondary">ไม่มีที่ว่างให้จัด</td></tr>
                  )}
                </tbody>
              </Table>

              {!!autoPreview.leftover.length && (
                <>
                  <div className="fw-semibold mb-1 text-danger">
                    นักเรียนที่ยังจัดไม่ได้ ({autoPreview.leftover.length} คน) — ที่นั่งไม่พอในกลุ่มเพศ/ระดับของตัวเอง
                  </div>
                  <Table size="sm" bordered className="mb-0 align-middle">
                    <thead><tr><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ชั้น</th><th>เพศ</th></tr></thead>
                    <tbody>
                      {autoPreview.leftover.slice(0, 50).map(student => (
                        <tr key={student.st_id}>
                          <td>{student.st_idnumber}</td>
                          <td>{student.st_name}</td>
                          <td>{student.c_class}/{student.r_no}</td>
                          <td>{student.st_gen === "m" ? "ชาย" : "หญิง"}</td>
                        </tr>
                      ))}
                      {autoPreview.leftover.length > 50 && (
                        <tr><td colSpan="4" className="text-center text-secondary">
                          ... และอีก {autoPreview.leftover.length - 50} คน
                        </td></tr>
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAutoModal(false)}>ยกเลิก</Button>
          <Button disabled={autoBusy || !autoPreview?.assignments.length} onClick={runAutoAssign}>
            ยืนยันจัดชุมนุม{autoPreview ? ` (${autoPreview.assignments.length} คน)` : ""}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={masterModal} onHide={() => setMasterModal(false)} centered>
        <Form onSubmit={saveMaster}>
          <Modal.Header closeButton><Modal.Title>{editingMaster ? "แก้ไขแม่แบบ" : "เพิ่มแม่แบบชุมนุม"}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label>รหัสชุมนุม</Form.Label><Form.Control required value={masterForm.club_code} onChange={e => setMasterForm({ ...masterForm, club_code: e.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>ชื่อชุมนุม</Form.Label><Form.Control required value={masterForm.club_name} onChange={e => setMasterForm({ ...masterForm, club_name: e.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>รายละเอียด</Form.Label><Form.Control as="textarea" rows={3} value={masterForm.club_description} onChange={e => setMasterForm({ ...masterForm, club_description: e.target.value })} /></Form.Group>
            <Form.Select value={masterForm.club_status} onChange={e => setMasterForm({ ...masterForm, club_status: e.target.value })}><option value="active">ใช้งาน</option><option value="inactive">ปิดใช้งาน</option></Form.Select>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setMasterModal(false)}>ยกเลิก</Button><Button type="submit">บันทึก</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={offeringModal} onHide={() => setOfferingModal(false)} size="lg" centered>
        <Form onSubmit={saveOffering}>
          <Modal.Header closeButton><Modal.Title>{editingOffering ? "แก้ไขชุมนุมประจำปี" : "เปิดชุมนุมประจำปี"}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}><Form.Label>แม่แบบ</Form.Label><Form.Select required disabled={!!editingOffering} value={offeringForm.club_id} onChange={e => selectMaster(e.target.value)}><option value="">เลือกแม่แบบ</option>{masters.filter(m => m.club_status === "active" || String(m.club_id) === String(offeringForm.club_id)).map(m => <option key={m.club_id} value={m.club_id}>{m.club_code} - {m.club_name}</option>)}</Form.Select></Col>
              <Col md={3}><Form.Label>เพศ</Form.Label><Form.Select value={offeringForm.club_gender} onChange={e => setOfferingForm({ ...offeringForm, club_gender: e.target.value })}><option value="m">ชาย</option><option value="f">หญิง</option></Form.Select></Col>
              <Col md={3}><Form.Label>โควตา</Form.Label><Form.Control type="number" min="1" required value={offeringForm.club_quota} onChange={e => setOfferingForm({ ...offeringForm, club_quota: e.target.value })} /></Col>
              <Col md={8}><Form.Label>ชื่อที่แสดงในปีนี้</Form.Label><Form.Control required value={offeringForm.club_name} onChange={e => setOfferingForm({ ...offeringForm, club_name: e.target.value })} /></Col>
              <Col md={4}><Form.Label>สถานะ</Form.Label><Form.Select value={offeringForm.club_status} onChange={e => setOfferingForm({ ...offeringForm, club_status: e.target.value })}><option value="active">เปิดใช้งาน</option><option value="inactive">ปิดใช้งาน</option></Form.Select></Col>
              <Col md={12}><Form.Label>รายละเอียด</Form.Label><Form.Control as="textarea" rows={2} value={offeringForm.club_description} onChange={e => setOfferingForm({ ...offeringForm, club_description: e.target.value })} /></Col>
              <Col md={6}><Form.Label>ครูผู้ดูแลหลัก</Form.Label><Form.Select required value={offeringForm.lead_t_id} onChange={e => setOfferingForm({ ...offeringForm, lead_t_id: e.target.value })}><option value="">เลือกครูหลัก</option>{refs.teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}</Form.Select></Col>
              <Col md={6}><Form.Label>ครูผู้ช่วย (เลือกได้หลายคน)</Form.Label><Form.Select multiple value={offeringForm.assistant_t_ids} onChange={e => setOfferingForm({ ...offeringForm, assistant_t_ids: Array.from(e.target.selectedOptions, o => o.value) })} style={{ minHeight: 110 }}>{refs.teachers.map(t => <option key={t.t_id} value={String(t.t_id)}>{t.t_name}</option>)}</Form.Select></Col>
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setOfferingModal(false)}>ยกเลิก</Button><Button type="submit">บันทึก</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!membersOffering} onHide={closeMembers} size="xl">
        <Modal.Header closeButton><Modal.Title>สมาชิก: {membersOffering?.club_name}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Form.Control placeholder="รหัสหรือชื่อนักเรียน อย่างน้อย 2 ตัวอักษร" value={studentQuery} onChange={e => setStudentQuery(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); searchStudents(); } }} />
            <Button onClick={searchStudents}>ค้นหา</Button>
          </div>
          {!!studentResults.length && (
            <div className="border rounded mb-3">
              {studentResults.map(student => (
                <div key={student.st_id} className="d-flex justify-content-between align-items-center p-2 border-bottom">
                  <span>{student.st_idnumber} {student.st_name} ({student.c_class}/{student.r_no}) {student.current_club_name && <Badge bg="warning" text="dark">มีชุมนุม: {student.current_club_name}</Badge>}</span>
                  <Button size="sm" disabled={!!student.current_club_year_id} onClick={() => addStudent(student)}>เพิ่ม</Button>
                </div>
              ))}
            </div>
          )}
          <Table responsive hover size="sm" className="align-middle">
            <thead><tr><th>รหัส</th><th>ชื่อ</th><th>ห้อง</th><th>เข้า/ขาด/สาย/ลา</th><th>ผล 1</th><th>ผล 2</th><th>ย้ายไป</th><th /></tr></thead>
            <tbody>
              {members.filter(m => m.enrollment_status === "active").map(member => (
                <tr key={member.club_enrollment_id}>
                  <td>{member.st_idnumber}</td><td>{member.st_name}</td><td>{member.c_class}/{member.r_no}</td>
                  <td>{member.presents || 0}/{member.absents || 0}/{member.lates || 0}/{member.leaves || 0}</td>
                  <td>{member.assessment_sem1 || "pending"}</td><td>{member.assessment_sem2 || "pending"}</td>
                  <td><Form.Select size="sm" defaultValue="" onChange={e => transfer(member, e.target.value)}><option value="">เลือก...</option>{offerings.filter(o => o.club_year_id !== membersOffering?.club_year_id && o.club_gender === member.st_gen && o.club_status === "active").map(o => <option key={o.club_year_id} value={o.club_year_id}>{o.club_name} ({o.enrolled}/{o.club_quota})</option>)}</Form.Select></td>
                  <td><Button size="sm" variant="outline-danger" onClick={() => withdraw(member)}>ถอน</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
      </main>
    </>
  );
}

function Dashboard({ data, year }) {
  const s = data.summary || {};
  const cards = [
    ["ชุมนุมทั้งหมด", s.total_clubs || 0, "primary"],
    ["ชุมนุมชาย / หญิง", `${s.male_clubs || 0} / ${s.female_clubs || 0}`, "info"],
    ["สมัครแล้ว / โควตา", `${s.enrolled || 0} / ${s.total_quota || 0}`, "success"],
    ["ที่ว่าง", s.vacancies || 0, "warning"],
    ["ยังไม่มีชุมนุม", s.unassigned_students || 0, "danger"],
    ["ไม่มีครูผู้ดูแล", s.clubs_without_advisor || 0, "secondary"],
    ["ผลประเมินค้าง", s.pending_assessments || 0, "dark"],
  ];
  return (
    <>
      <Row className="g-3 mb-3">
        {cards.map(([label, value, color]) => <Col sm={6} lg={3} key={label}><Card className="card-one h-100"><Card.Body><div className={`fs-3 fw-semibold text-${color}`}>{value}</div><div className="text-secondary">{label}</div></Card.Body></Card></Col>)}
      </Row>
      <Card className="card-one">
        <Card.Header><Card.Title as="h6" className="mb-0">อัตราสมัคร ปีการศึกษา {year?.year_year}</Card.Title></Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0"><thead><tr><th>ชุมนุม</th><th>เพศ</th><th>ครูหลัก</th><th>สมัคร</th><th>คงเหลือ</th></tr></thead><tbody>
            {(data.offerings || []).map(row => <tr key={row.club_year_id}><td>{row.club_name}</td><td>{row.club_gender === "m" ? "ชาย" : "หญิง"}</td><td>{row.lead_advisor || "-"}</td><td>{row.enrolled}/{row.club_quota}</td><td>{Math.max(0, row.club_quota - row.enrolled)}</td></tr>)}
          </tbody></Table>
        </Card.Body>
      </Card>
    </>
  );
}
